type TranslationMessages = {
  [key: string]: string
}

type Translations = {
  [locale: string]: TranslationMessages
}

type InterpolationParams = {
  [key: string]: string | number
}

interface I18nConfig {
  fallbackLocale?: string
  silentWarnings?: boolean
  onLocaleChange?: (locale: string) => void
}

const translations: Translations = {
  // 中文
  'zh-CN': {
    open: '打开菜单',
    feedback: '工单提报',
    title: '标题',
    desc: '描述',
    attachments: '附件',
    upload: '点击上传',
    submit: '提交',
    cancel: '取消',
    pleaseInput: '请填写',
    submitSucess: '提交成功',
    submitFailed: '提交失败',
    uploadFailed: '上传失败',
    loginFirst: '请先登录',
  },
  // 英语
  'en-US': {
    open: 'Open Menu',
    feedback: 'Ticket Submission',
    title: 'Title',
    desc: 'Description',
    attachments: 'Attachments',
    upload: 'Click to Upload',
    submit: 'Submit',
    cancel: 'Cancel',
    pleaseInput: 'Please enter',
    submitSucess: 'Submit Success',
    submitFailed: 'Submit Failed',
    uploadFailed: 'Upload Failed',
    loginFirst: 'Please login first'
  },
  // 法语
  'fr-FR': {
    open: 'Ouvrir le menu',
    feedback: 'Déclaration de Ticket',
    title: 'Titre',
    desc: 'Description',
    attachments: 'Pièces jointes',
    upload: 'Cliquez pour télécharger',
    submit: 'Soumettre',
    cancel: 'Annuler',
    pleaseInput: 'Veuillez entrer',
    submitSucess: 'Envoi réussi',
    submitFailed: "Échec de l'envoi",
    uploadFailed: 'Échec du téléchargement',
    loginFirst: 'Veuillez vous connecter d\'abord'
  },
  // 西语
  'es-ES': {
    open: 'Abrir el menú',
    feedback: 'Envío de Ticket',
    title: 'Título',
    desc: 'Descripción',
    attachments: 'Adjuntos',
    upload: 'Haz clic para subir',
    submit: 'Enviar',
    cancel: 'Cancelar',
    pleaseInput: 'Por favor ingresa',
    submitSucess: 'Envío exitoso',
    submitFailed: 'Envío fallido',
    uploadFailed: 'Error al subir',
    loginFirst: 'Por favor inicie sesión primero'
  },
  // 俄语
  'ru-RU': {
    open: 'Открыть меню',
    feedback: 'Отправка тикета',
    title: 'Заголовок',
    desc: 'Описание',
    attachments: 'Вложения',
    upload: 'Нажмите для загрузки',
    submit: 'Отправить',
    cancel: 'Отмена',
    pleaseInput: 'Пожалуйста введите',
    submitSucess: 'Отправлено успешно',
    submitFailed: 'Отправка не удалась',
    uploadFailed: 'Ошибка загрузки',
    loginFirst: 'Пожалуйста,войдите'
  },
  // 斯瓦希里语
  'sw-KE': {
    open: 'Fungua menyu',
    feedback: 'Wasilisha Tiketi',
    title: 'Kichwa',
    desc: 'Maelezo',
    attachments: 'Vifaa Vilivyohifadhiwa',
    upload: 'Bonyeza kuwasilisha',
    submit: 'Wasilisha',
    cancel: 'Ghairi',
    pleaseInput: 'Tafadhali ingiza',
    submitSucess: 'Wasilishwa kwa mafanikio',
    submitFailed: 'Wasilishaji haukufanikiwa',
    uploadFailed: 'Uwasilishaji umefeli',
    loginFirst: 'Tafadhali uendelea'
  }
}

/**
 * 语言代码别名映射表
 * 支持各种常见的语言代码变体
 */
const localeAliases: Record<string, string> = {
  // 中文变体
  'zh': 'zh-CN',
  'zh-cn': 'zh-CN',
  'cn': 'zh-CN',
  'CN': 'zh-CN',
  'chinese': 'zh-CN',
  'zh-Hans': 'zh-CN',
  'zh-hans': 'zh-CN',

  // 英文变体
  'en': 'en-US',
  'en-us': 'en-US',
  'us': 'en-US',
  'US': 'en-US',
  'english': 'en-US',
  'en-GB': 'en-US', // 英式英语也映射到美式英语
  'en-gb': 'en-US',

  // 法语变体
  'fr': 'fr-FR',
  'fr-fr': 'fr-FR',
  'french': 'fr-FR',

  // 西班牙语变体
  'es': 'es-ES',
  'es-es': 'es-ES',
  'spanish': 'es-ES',

  // 俄语变体
  'ru': 'ru-RU',
  'ru-ru': 'ru-RU',
  'russian': 'ru-RU',

  // 斯瓦希里语变体
  'sw': 'sw-KE',
  'sw-ke': 'sw-KE',
  'swahili': 'sw-KE'
}

/**
 * 规范化语言代码
 * 将各种格式的语言代码转换为标准格式
 * @param locale 原始语言代码
 * @returns 规范化后的语言代码，如果找不到则返回 null
 */
function normalizeLocale(locale: string): string | null {
  if (!locale) return null

  // 去除空格并转换为小写进行匹配
  const trimmedLocale = locale.trim()

  // 1. 直接匹配（精确匹配，保留大小写）
  if (translations[trimmedLocale]) {
    return trimmedLocale
  }

  // 2. 检查别名映射（不区分大小写）
  const lowerLocale = trimmedLocale.toLowerCase()
  if (localeAliases[lowerLocale]) {
    return localeAliases[lowerLocale]
  }

  // 3. 尝试标准化格式（如 zh_CN -> zh-CN）
  const standardized = trimmedLocale.replace('_', '-')
  if (translations[standardized]) {
    return standardized
  }

  // 4. 尝试小写转标准格式（如 zh-cn -> zh-CN）
  const parts = standardized.split('-')
  if (parts.length === 2) {
    const normalized = `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`
    if (translations[normalized]) {
      return normalized
    }
  }

  // 5. 尝试只匹配语言代码前缀（如 zh-TW -> zh-CN）
  const langPrefix = parts[0].toLowerCase()
  const matchedKey = Object.keys(translations).find((key) =>
    key.toLowerCase().startsWith(langPrefix + '-')
  )

  if (matchedKey) {
    return matchedKey
  }

  // 6. 检查别名中是否有前缀匹配
  const aliasMatch = Object.keys(localeAliases).find(
    (alias) => alias.toLowerCase() === langPrefix
  )

  if (aliasMatch) {
    return localeAliases[aliasMatch]
  }

  return null
}

function detectBrowserLocale(): string {
  const browserLang = navigator.language || 'en'

  // 使用规范化函数
  const normalized = normalizeLocale(browserLang)
  if (normalized) {
    return normalized
  }

  // 如果都找不到，返回默认语言
  return config.fallbackLocale || 'en-US'
}

let config: I18nConfig = {
  fallbackLocale: 'en-US',
  silentWarnings: false
}

let currentLocale = detectBrowserLocale()

function warn(message: string): void {
  if (!config.silentWarnings) {
    console.warn(`[i18n] ${message}`)
  }
}

function interpolate(template: string, params: InterpolationParams): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? String(params[key]) : match
  })
}

/**
 * 获取翻译文本
 * @param locale 语言代码（支持各种格式）
 * @param key 翻译键
 * @param params 插值参数（可选）
 */
export function getTranslation(
  locale: string,
  key: string,
  params?: InterpolationParams
): string {
  // 规范化语言代码
  const normalizedLocale = normalizeLocale(locale)

  if (!normalizedLocale) {
    warn(
      `Locale '${locale}' not found, falling back to '${config.fallbackLocale}'`
    )
    locale = config.fallbackLocale || 'en-US'
  } else {
    locale = normalizedLocale
  }

  let text = translations[locale]?.[key]

  if (!text) {
    warn(`Key '${key}' not found in locale '${locale}'`)

    if (locale !== config.fallbackLocale) {
      text = translations[config.fallbackLocale!]?.[key]
    }

    if (!text) {
      warn(
        `Key '${key}' not found in fallback locale '${config.fallbackLocale}'`
      )
      return key
    }
  }

  return params ? interpolate(text, params) : text
}

/**
 * 使用当前语言获取翻译
 * @param key 翻译键
 * @param params 插值参数
 */
export function t(key: string, params?: InterpolationParams): string {
  return getTranslation(currentLocale, key, params)
}

/**
 * 设置当前语言
 * @param locale 语言代码（支持 zh-CN, zh-cn, zh, cn, CN 等格式）
 */
export function setLocale(locale: string): boolean {
  // 规范化语言代码
  const normalizedLocale = normalizeLocale(locale)

  if (!normalizedLocale) {
    warn(`Cannot set locale '${locale}': locale not recognized or not registered`)
    return false
  }

  const oldLocale = currentLocale
  currentLocale = normalizedLocale

  // 触发回调
  if (config.onLocaleChange && oldLocale !== normalizedLocale) {
    config.onLocaleChange(normalizedLocale)
  }

  return true
}

/**
 * 获取当前语言
 */
export function getLocale(): string {
  return currentLocale
}

/**
 * 获取所有可用语言（包括别名）
 * @param includeAliases 是否包含别名，默认 false
 */
export function getAvailableLocales(includeAliases: boolean = false): string[] {
  const locales = Object.keys(translations)

  if (includeAliases) {
    return [...locales, ...Object.keys(localeAliases)]
  }

  return locales
}

/**
 * 添加或更新翻译
 * @param locale 语言代码
 * @param messages 翻译消息对象
 * @param merge 是否合并（默认 true）
 */
export function addTranslation(
  locale: string,
  messages: TranslationMessages,
  merge: boolean = true
): void {
  // 规范化语言代码
  const normalizedLocale = normalizeLocale(locale) || locale

  if (merge && translations[normalizedLocale]) {
    translations[normalizedLocale] = {
      ...translations[normalizedLocale],
      ...messages
    }
  } else {
    translations[normalizedLocale] = messages
  }
}

/**
 * 批量添加翻译
 */
export function addTranslations(newTranslations: Translations): void {
  Object.keys(newTranslations).forEach((locale) => {
    addTranslation(locale, newTranslations[locale])
  })
}

/**
 * 添加语言代码别名
 * @param alias 别名
 * @param target 目标语言代码
 */
export function addLocaleAlias(alias: string, target: string): boolean {
  const normalizedTarget = normalizeLocale(target)

  if (!normalizedTarget) {
    warn(`Cannot add alias '${alias}': target locale '${target}' not found`)
    return false
  }

  localeAliases[alias.toLowerCase()] = normalizedTarget
  return true
}

/**
 * 移除语言
 */
export function removeLocale(locale: string): boolean {
  const normalizedLocale = normalizeLocale(locale)

  if (!normalizedLocale) {
    return false
  }

  if (normalizedLocale === config.fallbackLocale) {
    warn(`Cannot remove fallback locale '${normalizedLocale}'`)
    return false
  }

  if (translations[normalizedLocale]) {
    delete translations[normalizedLocale]

    if (currentLocale === normalizedLocale) {
      currentLocale = config.fallbackLocale || 'en-US'
    }

    return true
  }

  return false
}

/**
 * 检查翻译键是否存在
 */
export function hasKey(key: string, locale?: string): boolean {
  const targetLocale = locale ? normalizeLocale(locale) || locale : currentLocale
  return !!translations[targetLocale]?.[key]
}

/**
 * 配置 i18n
 */
export function configure(options: I18nConfig): void {
  config = { ...config, ...options }

  if (options.fallbackLocale) {
    const normalized = normalizeLocale(options.fallbackLocale)
    if (normalized) {
      config.fallbackLocale = normalized
    } else {
      warn(`Fallback locale '${options.fallbackLocale}' not registered`)
    }
  }
}

/**
 * 获取所有翻译
 */
export function getTranslations(): Readonly<Translations> {
  return translations
}

/**
 * 获取所有语言别名映射
 */
export function getLocaleAliases(): Readonly<Record<string, string>> {
  return localeAliases
}

export type {
  Translations,
  TranslationMessages,
  InterpolationParams,
  I18nConfig
}

export const setLanguage = setLocale
